import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { AddressDropdown } from './address-dropdown';
import style from './body-header.module.less';
import AddressValidationSection from '../../components/address-validation';
import OrderNumberLink from '../../components/order-number-link';
import { ResponsiveTagList } from '../../components/responsive-tag-list';
import { emptyArray } from 'ui-components/default-props/empty';
import { useEntitiesFulfillmentPlanProperty } from '../../hooks/entities/selectors/fulfillment-plans';
import Global from '../../store/global-selectors';
import { messages } from '../messages';

export const ScanBodyHeader = ({ shipmentNumber = '' }) => {
  const fulfillmentPlanId = useSelector(Global.selectScanToPrintFulfillmentPlanId);
  const queue = useSelector(Global.selectScanToPrintQueue);
  const [salesOrderId] = useSelector(
    (state) =>
      Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId,
        property: 'salesOrdersFulfilledIds',
      }) || [],
  );
  const shipToAddress = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId: shipmentNumber,
      property: 'shipTo',
    }),
  );
  const orderNumber = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'orderNumber',
    }),
  );

  const fulfillmentPlanSetId = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'fulfillmentPlanSetId',
    }),
  );
  const fulfillmentPlanSetName = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanSetProperty(state, {
      fulfillmentPlanSetId,
      property: 'fulfillmentPlanSetName',
    }),
  );

  const toteId = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'toteId',
    }),
  );

  const tote = useSelector((state) =>
    toteId
      ? Global.selectSellerToteById(state, {
          toteId,
        })
      : null,
  );

  const addressString = shipToAddress
    ? [shipToAddress?.city, shipToAddress?.state, shipToAddress?.postalCode, shipToAddress?.countryCode].filter(Boolean).join(' ')
    : messages.addressPlaceholder;

  const fulfillmentPlanTagIds = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId ?? 0, 'tagIds') || emptyArray;
  const allSellerTags = useSelector(Global.selectSellerTags) || emptyArray;
  const fulfillmentPlanTags = useMemo(() => {
    return fulfillmentPlanTagIds.reduce((acc, tagId) => {
      const tag = allSellerTags.find((sellerTag) => sellerTag.tagId === tagId);
      if (tag) {
        acc.push(tag);
      }
      return acc;
    }, []);
  }, [fulfillmentPlanTagIds, allSellerTags]);

  return (
    <FlexColumn className={style['body-header']} gap={Gap.Space8}>
      <FlexRow className={style['data-rows']}>
        {fulfillmentPlanSetName ? (
          <FlexRow className={style['data-pair']}>
            <Text isBold textType={TextType.H3} value={messages.headerBatch} />
            <Text textType={TextType.H3} value={fulfillmentPlanSetName} />
          </FlexRow>
        ) : null}
        {queue?.isActive ? (
          <FlexRow className={style['data-pair']}>
            <Text isBold textType={TextType.H3} value={messages.queueProgressLabel} />
            <Text
              formattedMessageValues={{
                currentIndex: queue.currentIndex + 1,
                totalCount: queue.fulfillmentPlanIds.length,
              }}
              textType={TextType.H3}
              value={messages.queueProgress}
            />
          </FlexRow>
        ) : null}
        <FlexRow className={style['data-pair']}>
          <Text isBold textType={TextType.H3} value={messages.headerOrder} />
          {salesOrderId && fulfillmentPlanId ? (
            <OrderNumberLink
              buttonClassName={style['order-button']}
              id={fulfillmentPlanId}
              orderNumber={orderNumber}
              salesOrderId={salesOrderId}
            />
          ) : (
            <Text textType={TextType.H3} value={orderNumber} />
          )}
        </FlexRow>
        {tote && (
          <FlexRow className={style['data-pair']}>
            <Text isBold textType={TextType.H3} value={messages.headerTote} />
            <Text textType={TextType.H3} value={tote.toteName} />
          </FlexRow>
        )}
        <FlexRow className={style['data-pair']}>
          <Text isBold textType={TextType.H3} value={messages.headerShipment} />
          <Text textType={TextType.H3} value={shipmentNumber} />
        </FlexRow>
        <FlexRow className={style['data-pair']}>
          <Text isBold textType={TextType.H3} value={messages.headerShipTo} />
          <Text textType={TextType.H3} value={addressString} />
          <AddressValidationSection
            countryCode={shipToAddress?.countryCode}
            residentialIndicator={shipToAddress?.residentialIndicator}
            shipToAddress={shipToAddress}
            validationMessage='validate_address'
          />
          <AddressDropdown fulfillmentPlanId={shipmentNumber} salesOrderId={salesOrderId} />
        </FlexRow>
      </FlexRow>
      {fulfillmentPlanTags?.length > 0 && (
        <FlexRow className={style['data-pair']}>
          <Text isBold textType={TextType.H3} value={messages.tagsLabel} />
          <div className={style['tag-container']}>
            <ResponsiveTagList tags={fulfillmentPlanTags} />
          </div>
        </FlexRow>
      )}
    </FlexColumn>
  );
};

ScanBodyHeader.propTypes = {
  shipmentNumber: PropTypes.string,
};
